import { useSyncExternalStore } from 'react';
import { ApiError, api, call, setTokenProvider } from '../api/client';
import type { LoginRequest, LoginResponse, User } from '../api/types';
export type SessionState =
  | { status: 'unknown'; user: null }
  | { status: 'anonymous'; user: null }
  | { status: 'authenticated'; user: User };

let state: SessionState = { status: 'unknown', user: null };
let accessToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let inflightRefresh: Promise<boolean> | null = null;
const listeners = new Set<() => void>();

function emit(next: SessionState) {
  state = next;
  listeners.forEach((l) => l());
}

/**
 * The signed-in user (never a token) so the app can still render cached mail after an offline
 * reload — the refresh cookie cannot be exchanged without a network, and every route gates on a
 * session before it will show anything.
 */
const USER_KEY = 'mail.session.user';
const rememberUser = (user: User) => {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    // private mode or storage disabled: offline reload will land on the login page instead
  }
};
const forgetUser = () => {
  try {
    localStorage.removeItem(USER_KEY);
  } catch {
    /* nothing to do */
  }
};
const lastUser = (): User | null => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
};

function accept(r: LoginResponse) {
  rememberUser(r.user);
  accessToken = r.accessToken;
  clearTimeout(refreshTimer);
  // Refresh a minute before expiry so a busy admin never sees a 401 round-trip.
  refreshTimer = setTimeout(() => void refresh(), Math.max(5, r.expiresIn - 60) * 1000);
  emit({ status: 'authenticated', user: r.user });
}

function drop() {
  forgetUser();
  accessToken = null;
  clearTimeout(refreshTimer);
  emit({ status: 'anonymous', user: null });
}

/** Rotates the refresh cookie into a new access token. Coalesces concurrent callers. */
export function refresh(): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    const r = await api.POST('/auth/refresh');
    if (r.data) {
      accept(r.data);
      return true;
    }
    drop();
    return false;
  })().finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

/** Called once at boot: turns the refresh cookie (if any) into a session. Never throws. */
export async function init(): Promise<SessionState> {
  if (state.status !== 'unknown') return state;
  try {
    await refresh();
  } catch {
    // `refresh` only throws when the request never reached the server — a rejected session returns a
    // 401 and drops instead. Offline, keep the last known user so the service worker's cached mail is
    // reachable; there is no access token, so anything not already cached still fails.
    const cached = lastUser();
    if (cached) emit({ status: 'authenticated', user: cached });
    else drop();
  }
  return state;
}

export async function login(body: LoginRequest): Promise<User> {
  const r = await call(() => api.POST('/auth/login', { body }));
  accept(r);
  return r.user;
}

/**
 * Spend a hand-off from the admin panel and become the mailbox it names (ADR 0032).
 *
 * The two applications are separate origins and cannot share a cookie, so an administrator arrives
 * here with a token in the URL and no session. This exchanges it for one — a **mailbox** session,
 * marked with who is holding it, which is why nothing else in the client has to know about it.
 */
export async function redeemHandoff(token: string): Promise<User> {
  const r = await call(() => api.POST('/auth/handoff', { body: { token } }));
  accept(r);
  return r.user;
}

/**
 * Work an app wants done when its user signs out, beyond dropping the session itself.
 *
 * The session is shared; what has to be cleaned up is not. The webmail registers the task that empties
 * its offline mail cache — that cache is keyed by URL, so it outlives the session unless something
 * drops it — and the admin panel, which has no service worker and caches no mail, registers nothing.
 * Before the two apps were separated this module imported the mail client's cache directly, which is
 * the dependency that pointed the wrong way.
 */
const logoutTasks: Array<() => void> = [];
export function onLogout(task: () => void) {
  logoutTasks.push(task);
}

export async function logout(): Promise<void> {
  try {
    await api.POST('/auth/logout');
  } finally {
    drop();
    // a task that throws must not leave the rest of sign-out undone
    for (const task of logoutTasks) {
      try {
        task();
      } catch {
        /* nothing a signed-out user can act on */
      }
    }
  }
}

export function updateUser(user: User) {
  if (state.status === 'authenticated') emit({ status: 'authenticated', user });
}

/** Forget everything (tests; a hard sign-out uses `logout`). */
export function reset() {
  accessToken = null;
  clearTimeout(refreshTimer);
  inflightRefresh = null;
  state = { status: 'unknown', user: null };
}

export const getSession = () => state;
export const getAccessToken = () => accessToken;

/** Called on every change to the session, a token refresh included. Returns the unsubscribe. */
export function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useSession(): SessionState {
  return useSyncExternalStore(subscribe, getSession, getSession);
}

setTokenProvider({
  getToken: getAccessToken,
  refresh: () => refresh().catch(() => false),
});

export { ApiError };
